import type {
  Client,
  ClientOsint,
  GovernmentContractsStatus,
  OsintClientRecord,
  OsintImportResult,
} from '../types'

export interface WorkbookSheet {
  sheet: string
  data: unknown[][]
}

type RowRecord = Record<string, unknown>

function text(value: unknown): string {
  if (value instanceof Date) return value.toISOString()
  return String(value ?? '').trim()
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const normalized = text(value)
    .replace(/\s|\u00a0/g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '')
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function integer(value: unknown): number {
  return Math.max(0, Math.trunc(numberOrNull(value) ?? 0))
}

function normalizeHeader(value: unknown): string {
  return text(value)
    .toLocaleLowerCase('ru')
    .replace(/ё/g, 'е')
    .replace(/[._/\\-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

export function normalizeTaxId(value: unknown): string {
  return text(value).replace(/\D/g, '')
}

export function normalizeCompanyName(value: unknown): string {
  return text(value)
    .toLocaleLowerCase('ru')
    .replace(/ё/g, 'е')
    .replace(
      /^(?:общество с ограниченной ответственностью|публичное акционерное общество|акционерное общество|индивидуальный предприниматель|ооо|пао|ао|зао|оао|ип)\s+/,
      '',
    )
    .replace(/[^a-zа-я0-9]+/g, '')
}

function findHeaderIndex(data: unknown[][], required: string[]): number {
  const normalizedRequired = required.map(normalizeHeader)
  return data.slice(0, 12).findIndex((row) => {
    const headers = row.map(normalizeHeader)
    return normalizedRequired.every((requiredHeader) =>
      headers.includes(requiredHeader),
    )
  })
}

function rowsAsRecords(
  sheets: WorkbookSheet[],
  sheetName: string,
  requiredHeaders: string[],
): RowRecord[] {
  const sheet = sheets.find((item) => item.sheet === sheetName)
  if (!sheet) return []
  const headerIndex = findHeaderIndex(sheet.data, requiredHeaders)
  if (headerIndex < 0) return []
  const headers = sheet.data[headerIndex].map(normalizeHeader)

  return sheet.data
    .slice(headerIndex + 1)
    .filter((row) => row.some((value) => text(value)))
    .map((row) =>
      Object.fromEntries(headers.map((header, index) => [header, row[index]])),
    )
}

function firstByTaxId(rows: RowRecord[]): Map<string, RowRecord> {
  const map = new Map<string, RowRecord>()
  rows.forEach((row) => {
    const taxId = normalizeTaxId(row['инн'])
    if (taxId && !map.has(taxId)) map.set(taxId, row)
  })
  return map
}

function sourceUrls(...values: unknown[]): string[] {
  const urls = values
    .flatMap((value) => text(value).split(/\s+/))
    .filter((value) => /^https:\/\//i.test(value))
  return [...new Set(urls)]
}

function governmentStatus(value: unknown): GovernmentContractsStatus {
  const normalized = normalizeHeader(value)
  if (normalized === 'да' || normalized === 'найдено') return 'yes'
  if (
    normalized.includes('не найден') ||
    normalized.includes('не подтвержден')
  ) {
    return 'not_found'
  }
  return 'unknown'
}

function excelDate(value: unknown): string | null {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }
  if (typeof value === 'number' && value > 0) {
    const milliseconds = Math.round((value - 25569) * 86_400_000)
    const date = new Date(milliseconds)
    return Number.isFinite(date.getTime()) ? date.toISOString().slice(0, 10) : null
  }
  const valueText = text(value)
  if (!valueText) return null
  const parsed = Date.parse(valueText)
  return Number.isFinite(parsed) ? new Date(parsed).toISOString().slice(0, 10) : valueText
}

function collectedAt(sheets: WorkbookSheet[]): string {
  const method = sheets.find((sheet) => sheet.sheet === 'Методика')
  const sourceText = method?.data.flat().map(text).find((value) => value.includes('Дата сбора:'))
  const match = sourceText?.match(/(\d{2})\.(\d{2})\.(\d{4})/)
  return match ? `${match[3]}-${match[2]}-${match[1]}` : ''
}

export function isOsintWorkbook(sheets: WorkbookSheet[]): boolean {
  return sheets.some(
    (sheet) =>
      sheet.sheet === 'Рейтинг' &&
      findHeaderIndex(sheet.data, ['Клиент', 'ИНН', 'Категория выручки', 'Сегмент']) >= 0,
  )
}

export function parseOsintSheets(
  sheets: WorkbookSheet[],
  sourceFile: string,
): OsintClientRecord[] {
  const ranking = rowsAsRecords(
    sheets,
    'Рейтинг',
    ['Клиент', 'ИНН', 'Категория выручки', 'Сегмент'],
  )
  if (!ranking.length) {
    throw new Error('В отчёте не найден лист «Рейтинг» с данными клиентов.')
  }

  const contacts = firstByTaxId(
    rowsAsRecords(sheets, 'Контакты', ['Клиент', 'ИНН', 'Телефон']),
  )
  const contracts = firstByTaxId(
    rowsAsRecords(sheets, 'Тендеры и ЕИС', ['Клиент', 'ИНН', 'Реестровый номер']),
  )
  const tenderProfiles = firstByTaxId(
    rowsAsRecords(sheets, 'Тендерные площадки', ['Клиент', 'ИНН', 'Всего записей']),
  )
  const original = firstByTaxId(
    rowsAsRecords(sheets, 'Исходный список', ['Наименование', 'ИНН', 'КПП']),
  )
  const reportDate = collectedAt(sheets)

  return ranking.flatMap((row): OsintClientRecord[] => {
    const company = text(row['клиент'])
    const taxId = normalizeTaxId(row['инн'])
    if (!company || !taxId) return []

    const contact = contacts.get(taxId)
    const contract = contracts.get(taxId)
    const tender = tenderProfiles.get(taxId)
    const source = original.get(taxId)
    const contractUrl = text(contract?.['карточка еис'])
    const minimumContracts = integer(
      row['мин контрактов'] ?? contract?.['мин контрактов'],
    )
    const tenderTotal = integer(tender?.['всего записей'])

    const osint: ClientOsint = {
      collectedAt: reportDate,
      sourceFile,
      rank: numberOrNull(row['место']),
      legalType: text(row['тип']) === 'ИП' ? 'ИП' : text(row['тип']) === 'ЮЛ' ? 'ЮЛ' : '',
      revenue: numberOrNull(row['выручка, ₽']),
      revenueYear: numberOrNull(row['год']),
      revenueCategory: text(row['категория выручки']),
      segment: text(row['сегмент']),
      companyStatus: text(row['статус']),
      governmentContracts: governmentStatus(row['госконтракты']),
      minimumContracts,
      activity: text(row['основная деятельность']),
      completeness: text(row['полнота']),
      classDemand: text(source?.['класс спроса']),
      profileClass: text(source?.['класс профиля']),
      kpp: normalizeTaxId(source?.['кпп']),
      contact: {
        address: text(contact?.['адрес']),
        phone: text(contact?.['телефон']),
        email: text(contact?.['email']),
        website: text(contact?.['официальный сайт']),
        source: text(contact?.['источник']),
        comment: text(contact?.['комментарий']),
      },
      latestContract:
        contractUrl || text(contract?.['реестровый номер'])
          ? {
              laws: text(contract?.['законы']),
              date: excelDate(contract?.['дата контракта']),
              price: numberOrNull(contract?.['цена, ₽']),
              customer: text(contract?.['заказчик']),
              subject: text(contract?.['предмет объект']),
              registryNumber: text(contract?.['реестровый номер']),
              url: contractUrl,
            }
          : null,
      tenderProfile:
        tenderTotal > 0 || text(tender?.['карточка организации'])
          ? {
              matchedName: text(tender?.['наименование b2b house']),
              exactMatch: normalizeHeader(tender?.['совпадение']) === 'точное',
              winners: integer(tender?.['победители тендеров']),
              completed: integer(tender?.['завершенные тендеры']),
              total: tenderTotal,
              organizationUrl: text(tender?.['карточка организации']),
              searchUrl: text(tender?.['поиск по инн']),
            }
          : null,
      sources: sourceUrls(
        row['источник фин'],
        row['источник закупок'],
        contact?.['источник'],
        contract?.['источник checko'],
        contract?.['карточка еис'],
        tender?.['карточка организации'],
        tender?.['поиск по инн'],
      ),
    }

    return [{ company, taxId, osint }]
  })
}

function uniqueClientByName(clients: Client[]): Map<string, Client> {
  const grouped = new Map<string, Client[]>()
  clients.forEach((client) => {
    const key = normalizeCompanyName(client.company)
    if (!key) return
    grouped.set(key, [...(grouped.get(key) ?? []), client])
  })
  return new Map(
    [...grouped.entries()]
      .filter(([, matches]) => matches.length === 1)
      .map(([key, matches]) => [key, matches[0]]),
  )
}

export function mergeOsintRecords(
  records: OsintClientRecord[],
  existingClients: Client[],
  now = new Date().toISOString(),
): OsintImportResult {
  const byTaxId = new Map(
    existingClients
      .map((client) => [normalizeTaxId(client.taxId), client] as const)
      .filter(([taxId]) => taxId),
  )
  const byName = uniqueClientByName(existingClients)
  const replacements = new Map<string, Client>()
  const created: Client[] = []
  let updated = 0
  let skipped = 0

  records.forEach((record) => {
    const existing =
      byTaxId.get(record.taxId) ??
      byName.get(normalizeCompanyName(record.company))

    if (existing) {
      replacements.set(existing.id, {
        ...existing,
        company: existing.company || record.company,
        taxId: record.taxId,
        phone: existing.phone || record.osint.contact.phone,
        email: existing.email || record.osint.contact.email,
        segment: existing.segment || record.osint.segment,
        osint: record.osint,
        updatedAt: now,
      })
      updated += 1
      return
    }

    const duplicate = created.some(
      (client) =>
        client.taxId === record.taxId ||
        normalizeCompanyName(client.company) === normalizeCompanyName(record.company),
    )
    if (duplicate) {
      skipped += 1
      return
    }

    created.push({
      id: crypto.randomUUID(),
      company: record.company,
      taxId: record.taxId,
      contactPerson: '',
      phone: record.osint.contact.phone,
      email: record.osint.contact.email,
      city: '',
      segment: record.osint.segment,
      potential: null,
      status: 'new',
      important: false,
      attempts: 0,
      lastContactAt: null,
      nextContactAt: null,
      result: '',
      notes: '',
      interactions: [],
      osint: record.osint,
      createdAt: now,
      updatedAt: now,
    })
  })

  return {
    clients: [
      ...existingClients.map((client) => replacements.get(client.id) ?? client),
      ...created,
    ],
    created: created.length,
    updated,
    skipped,
  }
}

export function catalogSearchText(client: Client): string {
  const osint = client.osint
  return [
    client.company,
    client.taxId,
    client.contactPerson,
    client.phone,
    client.email,
    client.city,
    client.segment,
    osint?.segment,
    osint?.revenueCategory,
    osint?.legalType,
    osint?.companyStatus,
    osint?.activity,
    osint?.classDemand,
    osint?.profileClass,
    osint?.kpp,
    osint?.contact.address,
  ]
    .join(' ')
    .toLocaleLowerCase('ru')
}
